import EmatCombinedCategoryWorker from 'features/dataLayers/generation/workers/calculateEmatCombinedCategoryData.worker?worker'
import { useGetCdf2dDataFetcher } from 'features/dataLayers/load/useCdfRawValues2DArray'
import { getInspectionDate } from 'features/fde/inspectionDateHelpers'
import { useMemo } from 'react'
import {
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoInspection,
  GeckoMeasurementUnit,
  GeckoView,
  RolodexEntryType,
} from 'types'
import { DATE_FORMAT } from 'utils/fde/constants'

const PLACEHOLDER_THICKNESS = 1.0

/** Skip browser-derived CDFs when this CDS already has Parquet-backed EMAT derived layers. */
export function shouldSkipBrowserDerivedCdfs(
  cds: GeckoComponentDataset | undefined
): boolean {
  return !!cds?.hasPrecomputedEmatCategory
}

export function isMatchingPrecomputedEmatCategory(
  cdf: GeckoComponentDataFrame,
  selectedCDS: GeckoComponentDataset | undefined,
  selectedInspection?: GeckoInspection | undefined,
  componentId?: string | undefined
): boolean {
  return (
    isEmatCombinedCategoryCdf(cdf) &&
    !!cdf.data._parquet_column_name &&
    matchesEmatCategoryScope(cdf, selectedCDS, selectedInspection, componentId)
  )
}

export function getBrowserGeneratedEmatCombinedCategoryCdfs(
  componentDataFrames: readonly GeckoComponentDataFrame[] | undefined,
  selectedCDS?: GeckoComponentDataset,
  selectedInspection?: GeckoInspection,
  componentId?: string
): readonly GeckoComponentDataFrame[] {
  return (
    componentDataFrames?.filter(
      (cdf) =>
        isEmatCombinedCategoryCdf(cdf) &&
        !cdf.data._parquet_column_name &&
        matchesEmatCategoryScope(
          cdf,
          selectedCDS,
          selectedInspection,
          componentId
        )
    ) ?? []
  )
}

function isEmatCombinedCategoryCdf(cdf: GeckoComponentDataFrame): boolean {
  return (
    cdf.data.params.view.id === GeckoView.EmatCustomThicknessCondition.id &&
    cdf.data.params.measurement_unit.id ===
      GeckoMeasurementUnit.EmatCustomCategory.id
  )
}

function matchesEmatCategoryScope(
  cdf: GeckoComponentDataFrame,
  selectedCDS: GeckoComponentDataset | undefined,
  selectedInspection?: GeckoInspection,
  componentId?: string
): boolean {
  if (selectedCDS) {
    return cdf.tags[RolodexEntryType.COMPONENT_DATASET] === selectedCDS.id
  }
  if (
    selectedInspection?.id &&
    cdf.tags[RolodexEntryType.INSPECTION] !== selectedInspection.id
  ) {
    return false
  }
  if (componentId && cdf.tags[RolodexEntryType.COMPONENT] !== componentId) {
    return false
  }
  return true
}

export function useEmatCombinedCategoryData(
  componentDataFrames: readonly GeckoComponentDataFrame[] | undefined,
  selectedCDS: GeckoComponentDataset | undefined,
  selectedInspection?: GeckoInspection | undefined,
  componentId?: string | undefined
) {
  const ematCategoryCdf = useMemo(() => {
    const cdsIdToUse = selectedCDS?.id

    return componentDataFrames?.find((cdf) => {
      if (
        cdf.data?.params?.source?.id !== 'emat' ||
        cdf.data?.params?.view?.id !== 'emat-category'
      ) {
        return false
      }

      // Primary match: component-dataset (most stable / specific)
      if (cdsIdToUse) {
        return cdf.tags[RolodexEntryType.COMPONENT_DATASET] === cdsIdToUse
      }

      // Fallback match (when selectedCDS isn't available): use inspection/component tags if present
      if (
        selectedInspection?.id &&
        cdf.tags[RolodexEntryType.INSPECTION] &&
        cdf.tags[RolodexEntryType.INSPECTION] !== selectedInspection.id
      )
        return false

      if (
        componentId &&
        cdf.tags[RolodexEntryType.COMPONENT] &&
        cdf.tags[RolodexEntryType.COMPONENT] !== componentId
      )
        return false

      return true
    })
  }, [
    componentDataFrames,
    componentId,
    selectedCDS?.id,
    selectedInspection?.id,
  ])

  const differenceFromNominalCdfs = useMemo(() => {
    const cdsIdToUse =
      selectedCDS?.id ??
      ematCategoryCdf?.tags[RolodexEntryType.COMPONENT_DATASET] ??
      undefined

    return (
      componentDataFrames?.filter((cdf) => {
        const viewId = cdf.data?.params?.view?.id
        const isStandardDifferenceFromNominalView =
          viewId === GeckoView.DifferenceFromNominal.id

        const isPercentDfn =
          cdf.data?.params?.measurement_unit.id ===
            GeckoMeasurementUnit.Percent.id &&
          isStandardDifferenceFromNominalView

        if (!isPercentDfn) return false

        const matchesInspection =
          !selectedInspection?.id ||
          cdf.tags[RolodexEntryType.INSPECTION] === selectedInspection.id

        if (!matchesInspection) return false

        if (
          cdsIdToUse &&
          cdf.tags[RolodexEntryType.COMPONENT_DATASET] !== cdsIdToUse
        )
          return false

        return true
      }) ?? []
    )
  }, [
    ematCategoryCdf,
    componentDataFrames,
    selectedCDS?.id,
    selectedInspection?.id,
  ])

  const differenceFromNominalCdf = useMemo(() => {
    return differenceFromNominalCdfs?.at(0)
  }, [differenceFromNominalCdfs])

  const precomputedCombinedCategoryCdfs = useMemo(
    () =>
      componentDataFrames?.filter((cdf) =>
        isMatchingPrecomputedEmatCategory(
          cdf,
          selectedCDS,
          selectedInspection,
          componentId
        )
      ) ?? [],
    [componentDataFrames, componentId, selectedCDS, selectedInspection]
  )

  const fetchCdf2dArray = useGetCdf2dDataFetcher()

  return useMemo(
    () => ({
      ematCategoryCdf,
      differenceFromNominalCdf,
      differenceFromNominalCdfs,
      precomputedCombinedCategoryCdfs,
      fetchCdf2dArray,
    }),
    [
      fetchCdf2dArray,
      differenceFromNominalCdf,
      differenceFromNominalCdfs,
      ematCategoryCdf,
      precomputedCombinedCategoryCdfs,
    ]
  )
}

export async function callEmatCombinedCategoryWorker(
  referenceCdf: GeckoComponentDataFrame,
  selectedInspection: GeckoInspection | undefined,
  assetModel: GeckoAssetModel | undefined,
  configToUse: ForecastComponentConfig | undefined,
  componentSlug: string | undefined,
  ematCategoryData: readonly (readonly number[])[],
  differenceFromNominalData: readonly (readonly number[])[]
): Promise<number[][]> {
  if (
    !ematCategoryData ||
    ematCategoryData.length === 0 ||
    !differenceFromNominalData ||
    differenceFromNominalData.length === 0
  ) {
    throw new Error(
      'Required EMAT data not available for combined category calculation'
    )
  }

  // We need to get the actual thickness values from the reference CDF for bin iteration
  // For EMAT combined category, we can use the EMAT data dimensions or reference CDF data
  let thicknessValues: readonly (readonly number[])[] = []

  if (referenceCdf) {
    if (ematCategoryData.length > 0) {
      thicknessValues = ematCategoryData.map((row) =>
        row.map(() => PLACEHOLDER_THICKNESS)
      )
    }
  }

  const worker = new EmatCombinedCategoryWorker()

  return new Promise((resolve, reject) => {
    worker.onmessage = (event) => {
      if (event.data?.error) {
        console.error('EMAT worker returned error:', event.data.error)
        worker.terminate()
        reject(new Error(event.data.error))
        return
      }

      if (!Array.isArray(event.data)) {
        console.error('EMAT worker returned invalid data format:', event.data)
        worker.terminate()
        reject(new Error('Worker returned invalid data format'))
        return
      }

      worker.terminate()
      resolve(event.data)
    }

    worker.onerror = (error) => {
      console.error('EMAT worker error event:', error)
      worker.terminate()
      reject(new Error('Worker encountered an error'))
    }

    const workerParams = {
      componentId: componentSlug || '',
      assetModelJson: assetModel?.raw,
      thicknessValues,
      thicknessUnit: referenceCdf.data.params.measurement_unit.id.toString(),
      binHeightJson: referenceCdf.data.binning.bin_size.height.toJSON(),
      binWidthJson: referenceCdf.data.binning.bin_size.width.toJSON(),
      yGeometryStartJson: referenceCdf.data.geometry.start.y.toJSON(),
      xGeometryStartJson: referenceCdf.data.geometry.start.x.toJSON(),
      forecastComponentConfig: configToUse ?? {},
      inspectionDateString: selectedInspection
        ? getInspectionDate({
            inspectionSlug: referenceCdf.tags.inspection,
            inspection: selectedInspection,
          })?.format(DATE_FORMAT) || ''
        : '',
      ematCategoryData,
      differenceFromNominalData,
    }

    worker.postMessage(workerParams)
  })
}
